import _                        from 'underscore';
import classNames               from 'classnames';
import React, { FC, ReactNode } from 'react';
import styled                   from 'styled-components';
import { Icon, IconProps }      from 'semantic-ui-react';

import { CorvalUIColor, SemanticUIColor, SystemEvent } from '../../../../types';
import { getCSSVar }                                   from '../../../../util/BrowserUtility';
import { runIfFunction }                               from '../../../../util/FunctionalUtility';


type HarveyBallColors = string
                        | SemanticUIColor
                        | CorvalUIColor.WHITE
                        | CorvalUIColor.GRAY
                        | CorvalUIColor.OBSIDIAN
                        | CorvalUIColor.ONYX
                        | CorvalUIColor.LIGHTBLACK
                        | CorvalUIColor.LIGHTBLUE
                        | CorvalUIColor.LIGHTGREEN
                        | CorvalUIColor.LIGHTORANGE
                        | CorvalUIColor.LIGHTPINK
                        | CorvalUIColor.LIGHTOLIVE
                        | CorvalUIColor.LIGHTPURPLE
                        | CorvalUIColor.LIGHTRED
                        | CorvalUIColor.LIGHTTEAL
                        | CorvalUIColor.LIGHTYELLOW
                        | CorvalUIColor.BLUEBACKGROUND
                        | CorvalUIColor.GREENBACKGROUND
                        | CorvalUIColor.ORANGEBACKGROUND
                        | CorvalUIColor.PINKBACKGROUND
                        | CorvalUIColor.OLIVEBACKGROUND
                        | CorvalUIColor.PURPLEBACKGROUND
                        | CorvalUIColor.REDBACKGROUND
                        | CorvalUIColor.TEALBACKGROUND
                        | CorvalUIColor.YELLOWBACKGROUND
                        | CorvalUIColor.DARKBLUE
                        | CorvalUIColor.DARKGREEN
                        | CorvalUIColor.DARKORANGE
                        | CorvalUIColor.DARKPINK
                        | CorvalUIColor.DARKOLIVE
                        | CorvalUIColor.DARKPURPLE
                        | CorvalUIColor.DARKRED
                        | CorvalUIColor.DARKTEAL
                        | CorvalUIColor.DARKYELLOW
                        | CorvalUIColor.OFFWHITE
                        | CorvalUIColor.DARKWHITE
                        | CorvalUIColor.LIGHTGREY;

export interface HarveyBallProps {
    sectors: 0 | 1 | 2 | 3 | 4,
    size?: number,
    bgColor?: HarveyBallColors,
    color?: HarveyBallColors,
    borderColor?: HarveyBallColors,
    completedColor?: HarveyBallColors,
    completedIconProps?: IconProps,
    useIcon?: boolean,
    useBorders?: boolean,
    loading?: boolean,
    content?: ReactNode,

    onClick?( e?: SystemEvent ): void,

    [ additionalProps: string ]: any
}

const defaultProps: HarveyBallProps = {
    sectors:            0,
    size:               32,
    color:              SemanticUIColor.TEAL,
    bgColor:            CorvalUIColor.LIGHTGREY,
    borderColor:        CorvalUIColor.WHITE,
    completedColor:     SemanticUIColor.TEAL,
    completedIconProps: {
        name:     'check',
        size:     null,
        fitted:   true,
        inverted: true,
    },
    useIcon:            true,
    useBorders:         true,
    loading:            false,
    content:            null,
    onClick:            null,
}

const SectoredCircle = styled.div<HarveyBallProps & { width: any, height: any }>`
    position: relative;
    width: ${ props => props.width }px;
    height: ${ props => props.height }px;
    background: ${ props => props.bgColor };
    border-radius: 50%;
    overflow: hidden;
    display: inline-block;

    @keyframes harveyBallAnimation {
        0% {
            opacity: 0.1;
            transform: rotate(0deg);
        }
        25% {
            opacity: 0.4;
            transform: rotate(90deg);
        }
        50% {
            opacity: 0.5;
            transform: rotate(180deg);
        }
        75% {
            opacity: 0.4;
            transform: rotate(270deg);
        }
        100% {
            opacity: 0.1;
            transform: rotate(360deg);
        }
    }

    & > div {

        position: absolute;

        /* Sector Containers */

        &.hb-sector-1,
        &.hb-sector-2,
        &.hb-sector-3,
        &.hb-sector-4 {
            z-index: 1;
            width: ${ props => props.width }px;
            height: ${ props => props.height }px;
            background: ${ props => ( props.sectors === 4 ) ? ( props.completedColor ?? props.color ) : props.color };
        }

        &.hb-sector-1 {
            top: calc(-50%);
            left: calc(50%);
        }

        &.hb-sector-2 {
            top: calc(50%);
            left: calc(50%);
        }

        &.hb-sector-3 {
            top: calc(50%);
            left: calc(-50%);
        }

        &.hb-sector-4 {
            top: calc(-50%);
            left: calc(-50%);
        }

        /* Icon Container */

        &.hb-icon {
            z-index: 3;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: grid;
            align-items: center;
            justify-content: center;
        }

        &.hb-loading-spinner {
            z-index: 1;
            width: ${ props => props.width }px;
            height: ${ props => props.height }px;
            top: calc(-50%);
            left: calc(50%);
            background: #000;
            transform-origin: left bottom;
            animation: harveyBallAnimation 0.8s ease-in-out infinite;
            animation-duration: 0.8s !important;
        }

        &.hb-content-overlay {
            z-index: 4;
            top: 3px;
            left: 3px;
            bottom: 3px;
            right: 3px;
            width: calc(100% - 6px);
            height: calc(100% - 6px);
            border-radius: 50%;
            background: ${ props => props.borderColor ?? props.bgColor };
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    &:before, &:after {
        ${ props => ( !props.useBorders || ( props.sectors === 4 && props.useIcon ) ) ? '' : 'content: "";' }
        position: absolute;
        z-index: 2;
        top: 0;
        bottom: 0;
        left: calc(50% - 0.5px);
        width: 1px;
        height: 100%;
        background-color: ${ props => props.borderColor ?? props.bgColor };
    }

    &:before {

    }

    &:after {
        transform: rotate(90deg)
    }
`;

const HarveyBall: FC<HarveyBallProps> = ( props ) => {

    let color          = getCSSVar( `--color-${ props.color }`, props.color ),
        bgColor        = getCSSVar( `--color-${ props.bgColor }`, props.bgColor ),
        completedColor = getCSSVar( `--color-${ props.completedColor }`, props.completedColor ),
        borderColor    = getCSSVar( `--color-${ props.borderColor }`, props.borderColor );

    let styleProps = {
        ..._.pick( props,
            'bgColor',
            'sectors',
            'completedColor',
            'color',
            'borderColor',
            'bgColor',
            'useBorders',
            'useIcon',
        ),
        color,
        bgColor,
        completedColor,
        borderColor,
    }

    const handleOnClick = ( e: SystemEvent ) => {

        runIfFunction( props.onClick );
    }

    return (
        <SectoredCircle
            { ...styleProps }
            width={ props.size }
            height={ props.size }
            onClick={ handleOnClick }
            className={ classNames( {
                'cursor--pointer': _.isFunction( props.onClick )
            } ) }>

            { props.loading ? (
                <>
                    <div className={ 'hb-loading-spinner' }/>
                    <div className={ 'hb-content-overlay' }/>
                </>

            ) : (

                  <>
                      {/* Sector Elements */ }
                      { props.sectors > 0 && <div className={ 'hb-sector-1' }>&nbsp;</div> }
                      { props.sectors > 1 && <div className={ 'hb-sector-2' }>&nbsp;</div> }
                      { props.sectors > 2 && <div className={ 'hb-sector-3' }>&nbsp;</div> }
                      { props.sectors > 3 && <div className={ 'hb-sector-4' }>&nbsp;</div> }

                      {/* User content */ }
                      { props.content && <div className={ 'hb-content-overlay' }>{ props.content }</div> }

                      {/* Sectors "Full" Icon Element */ }
                      { props.sectors === 4 && props.useIcon && (
                          <div className={ 'hb-icon' }>
                              <Icon { ...props.completedIconProps }/>
                          </div>
                      ) }

                  </>
              ) }
        </SectoredCircle>
    );
};

const CriticalityHarveyBall: FC<HarveyBallProps> = ( props ) => {

    let criticalityThemes = {
        1: { color: SemanticUIColor.TEAL },
        2: { color: SemanticUIColor.TEAL },
        3: { color: SemanticUIColor.TEAL },
        4: { completedColor: SemanticUIColor.GREEN },
    }

    let criticalityProps = {
        ...props,
        ...( criticalityThemes[ props.sectors ] ?? {} )
    }

    return <HarveyBall { ...criticalityProps } />
}

const InactiveHarveyBall: FC<Omit<HarveyBallProps, 'sectors'>> = ( props ) => {

    let inactiveProps: HarveyBallProps = {
        sectors:                 props.loading ? 0 : 4,
        useIcon:                 true,
        useBorders:              false,
        completedColorfullColor: SemanticUIColor.GREY,
        completedIconProps:      { name: 'minus', inverted: true, fitted: true, style: { transform: `rotate(-45deg) scaleX(3) scaleY(0.6)` } },
    }

    let ballProps = {
        ...props,
        ...inactiveProps
    }

    return <HarveyBall { ...ballProps } />
}

HarveyBall.defaultProps = defaultProps;

export { HarveyBall, CriticalityHarveyBall, InactiveHarveyBall }
